<script setup lang="ts">
import type { Ref } from 'vue';
import type { LinkRecordsSubmit } from './LinkRecordsDialog.vue';
import type { MemberSearchSubmit } from './MemberSearchDialog.vue';
import type { Group, GroupRecord, SuggestionCluster } from '@/components/domain/record-link/fields';
import axios from 'axios';
import { Loader } from 'lucide';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphDialog } from '@/components/base/dialog';
import { SphField } from '@/components/base/field';
import { SphIcon } from '@/components/base/icon';
import { SphInput } from '@/components/base/input';
import { SphPagination } from '@/components/base/pagination';
import { SphSelect } from '@/components/base/select';
import { SphSpinner } from '@/components/base/spinner';
import { SphTabs } from '@/components/base/tabs';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';
import GroupCard from './GroupCard.vue';
import MemberSearchDialog from './MemberSearchDialog.vue';
import SuggestionsList from './SuggestionsList.vue';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
    canManage: boolean
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

const groups = ref<Group[]>([]);
const groupsTotal = ref(0);
const lastPipelineRunAt = ref<string | null>(null);
const hasMainStrategies = ref(false);
const clusters = ref<SuggestionCluster[]>([]);
const crossClubCount = ref(0);
const sameClubCount = ref(0);
const dismissedCount = ref(0);
const exactMatchCount = ref(0);
const autoLinkDialogOpen = ref(false);
const loading = ref(true);
const loadingSuggestions = ref(true);
const pendingAction = ref(false);
const filterState = ref<string | undefined>(undefined);
const filterSearch = ref('');
const createDialogOpen = ref(false);
const addTargetGroup = ref<Group | null>(null);
const addDialogOpen = ref(false);
const removeCandidate = ref<{ group: Group, record: GroupRecord } | null>(null);
const removeDialogOpen = ref(false);
const dissolveTarget = ref<Group | null>(null);
const dissolveDialogOpen = ref(false);
const activeTab = ref<'suggestions' | 'groups' | 'same-club' | 'dismissed'>('suggestions');
const expandAll = ref(false);

const PAGE_SIZE = 25;
const groupsPage = ref(1);
const crossClubPage = ref(1);
const sameClubPage = ref(1);
const dismissedPage = ref(1);

let searchDebounce: ReturnType<typeof setTimeout> | undefined;

const stateOptions = computed(() => [
    { value: 'unresolved', label: i18n.t('Ohne Hauptdatensatz') },
    { value: 'resolved', label: i18n.t('Hauptdatensatz festgelegt') },
    ...(hasMainStrategies.value
        ? [
                { value: 'conflict', label: i18n.t('Konflikt bei der Ermittlung') },
                { value: 'stale', label: i18n.t('Nicht mehr durch Regel gedeckt') },
            ]
        : []),
]);

function errorMessage(error: any): string {
    return error?.response?.data?.error?.message ?? i18n.t('Die Aktion konnte nicht ausgeführt werden.');
}

function suggestionsPageRef() {
    switch (activeTab.value) {
        case 'same-club': return sameClubPage;
        case 'dismissed': return dismissedPage;
        default: return crossClubPage;
    }
}

const suggestionsPage = computed(() => suggestionsPageRef().value);

function pagingParams(page: number) {
    return { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
}

const lastPageFor = (total: number) => Math.max(1, Math.ceil(total / PAGE_SIZE));
const suggestionsTotal = computed(() => {
    switch (activeTab.value) {
        case 'same-club': return sameClubCount.value;
        case 'dismissed': return dismissedCount.value;
        default: return crossClubCount.value;
    }
});

function loadGroups() {
    loading.value = true;
    axios.get(`/federations/${props.federationId}/record-links/groups`, {
        params: {
            ...pagingParams(groupsPage.value),
            state: filterState.value || undefined,
            search: filterSearch.value.trim() || undefined,
        },
    })
        .then((response) => {
            groupsTotal.value = response.data.meta?.total ?? response.data.data.length;
            lastPipelineRunAt.value = response.data.meta?.lastPipelineRunAt ?? null;
            hasMainStrategies.value = response.data.meta?.hasMainStrategies ?? false;
            if (groupsPage.value > lastPageFor(groupsTotal.value)) {
                groupsPage.value = lastPageFor(groupsTotal.value);
                loadGroups();
                return;
            }
            groups.value = response.data.data;
            loading.value = false;
        })
        .catch(() => {
            notify.error(i18n.t('error.loadingData'));
            loading.value = false;
        });
}

function loadSuggestions() {
    const categories: Record<string, string> = { 'same-club': 'same_club', dismissed: 'dismissed' };
    loadingSuggestions.value = true;
    axios.get(`/federations/${props.federationId}/record-links/suggestions`, {
        params: {
            category: categories[activeTab.value] ?? 'cross_club',
            ...pagingParams(suggestionsPage.value),
        },
    })
        .then((response) => {
            crossClubCount.value = response.data.meta?.crossClubCount ?? 0;
            sameClubCount.value = response.data.meta?.sameClubCount ?? 0;
            dismissedCount.value = response.data.meta?.dismissedCount ?? 0;
            exactMatchCount.value = response.data.meta?.exactMatchCount ?? 0;
            lastPipelineRunAt.value = response.data.meta?.lastPipelineRunAt ?? null;
            if (suggestionsPage.value > lastPageFor(suggestionsTotal.value)) {
                suggestionsPageRef().value = lastPageFor(suggestionsTotal.value);
                loadSuggestions();
                return;
            }
            clusters.value = response.data.data;
            loadingSuggestions.value = false;
        })
        .catch(() => {
            notify.error(i18n.t('error.loadingData'));
            loadingSuggestions.value = false;
        });
}

const JOB_POLL_INTERVAL = 5000;

type JobStatus = 'pending' | 'running' | null;

interface QueuedJob {
    status: Ref<JobStatus>
    poll: (_notifyWhenDone: boolean) => void
    stop: () => void
}

/** Follows one queued run of the federation on its status endpoint until it is done, then reloads both lists. */
function useQueuedJob(path: string, finishedMessage: string): QueuedJob {
    const status = ref<JobStatus>(null);
    let timer: ReturnType<typeof setTimeout> | undefined;

    function poll(notifyWhenDone: boolean) {
        clearTimeout(timer);
        axios.get(`/federations/${props.federationId}/record-links/${path}`)
            .then((response) => {
                const next: JobStatus = response.data.data.status ?? null;
                const finished = status.value !== null && next === null;
                status.value = next;
                if (next !== null) {
                    timer = setTimeout(poll, JOB_POLL_INTERVAL, notifyWhenDone);
                    return;
                }
                if (finished) {
                    if (notifyWhenDone) {
                        notify.success(i18n.t(finishedMessage));
                    }
                    loadSuggestions();
                    loadGroups();
                }
            })
            .catch(() => {
                // a failed poll is not worth a toast; keep watching while a run is known to be queued
                if (status.value !== null) {
                    timer = setTimeout(poll, JOB_POLL_INTERVAL, notifyWhenDone);
                }
            });
    }

    return { status, poll, stop: () => clearTimeout(timer) };
}

const scanJob = useQueuedJob('scan', 'Der Scan ist abgeschlossen.');
const autoLinkJob = useQueuedJob('suggestions/auto-link', 'Die automatische Verknüpfung ist abgeschlossen.');
const scanStatus = scanJob.status;
const autoLinkStatus = autoLinkJob.status;
const jobRunning = computed(() => scanStatus.value !== null || autoLinkStatus.value !== null);

onMounted(() => {
    loadGroups();
    loadSuggestions();
    scanJob.poll(false);
    autoLinkJob.poll(false);
});

onUnmounted(() => {
    scanJob.stop();
    autoLinkJob.stop();
    clearTimeout(searchDebounce);
});

watch(activeTab, (tab) => {
    if (tab === 'suggestions' || tab === 'same-club' || tab === 'dismissed') {
        loadSuggestions();
    }
});

watch(filterState, () => {
    groupsPage.value = 1;
    loadGroups();
});

watch(filterSearch, () => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
        groupsPage.value = 1;
        loadGroups();
    }, 300);
});

// A list that shrinks under the reader (a link, a scan, the auto-link) can leave the current page past its
// end. The empty page shows no pagination that could clamp it, so the loaders step back to the last page.
function onGroupsPage(page: number) {
    groupsPage.value = page;
    loadGroups();
}

function onSuggestionsPage(page: number) {
    suggestionsPageRef().value = page;
    loadSuggestions();
}

const hasDifferences = computed(() => (activeTab.value === 'groups'
    ? groups.value.some((group) => group.records.some((record) => record.divergentFields.length > 0))
    : clusters.value.some((cluster) => cluster.divergentFields.length > 0)));

const scanInfo = computed(() => {
    const parts: string[] = [];
    if (lastPipelineRunAt.value !== null) {
        parts.push(`${i18n.t('Letzter Scan')} ${lastPipelineRunAt.value}`);
    }
    parts.push(i18n.t('{count} Treffer', { count: suggestionsTotal.value }));
    if (activeTab.value === 'suggestions') {
        parts.push(i18n.t('trägerweit über alle Vereine'));
    }

    return parts.join(' · ');
});

const tabs = computed(() => [
    { value: 'suggestions', label: `${i18n.t('Vorschläge')} (${crossClubCount.value})` },
    { value: 'groups', label: `${i18n.t('Verknüpfte Datensätze')} (${groupsTotal.value})` },
    { value: 'dismissed', label: `${i18n.t('Nicht verknüpfte Datensätze')} (${dismissedCount.value})` },
    { value: 'same-club', label: `${i18n.t('Dubletten im gleichen Verein')} (${sameClubCount.value})` },
]);

function startScan() {
    pendingAction.value = true;
    axios.post(`/federations/${props.federationId}/record-links/scan`)
        .then((response) => {
            if (response.data.data.queued) {
                notify.success(i18n.t('Der Scan wurde gestartet. Die Vorschläge erscheinen in Kürze.'));
            } else {
                notify.info(i18n.t('Ein Scan läuft bereits.'));
            }
            scanStatus.value = scanStatus.value ?? 'pending';
            scanJob.poll(true);
        })
        .catch((error) => notify.error(errorMessage(error)))
        .finally(() => {
            pendingAction.value = false;
        });
}

function autoLink(event: { done: (_success: boolean) => void }) {
    pendingAction.value = true;
    axios.post(`/federations/${props.federationId}/record-links/suggestions/auto-link`)
        .then((response) => {
            if (response.data.data.queued) {
                notify.success(i18n.t('Die automatische Verknüpfung wurde gestartet.'));
            } else {
                notify.info(i18n.t('Eine automatische Verknüpfung läuft bereits.'));
            }
            autoLinkStatus.value = autoLinkStatus.value ?? 'pending';
            autoLinkJob.poll(true);
            event.done(true);
        })
        .catch((error) => {
            notify.error(errorMessage(error));
            event.done(false);
        })
        .finally(() => {
            pendingAction.value = false;
        });
}

const actionMessages = {
    confirm: 'Die Datensätze wurden verknüpft.',
    dismiss: 'Die Vorschläge wurden verworfen.',
    reopen: 'Die Vorschläge wurden wieder geöffnet.',
};

async function runSequentially(suggestionIds: number[], action: keyof typeof actionMessages) {
    pendingAction.value = true;
    let succeeded = 0;
    let firstError: string | null = null;
    for (const suggestionId of suggestionIds) {
        try {
            await axios.post(`/federations/${props.federationId}/record-links/suggestions/${suggestionId}/${action}`);
            succeeded++;
        } catch (error: any) {
            // earlier confirms auto-close the other pairs of the same person, so a 404 here means already done
            if (action === 'confirm' && error?.response?.status === 404) {
                succeeded++;
                continue;
            }
            firstError = firstError ?? errorMessage(error);
        }
    }

    pendingAction.value = false;
    if (succeeded > 0) {
        notify.success(i18n.t(actionMessages[action]));
    }
    if (firstError !== null) {
        notify.error(firstError);
    }

    loadSuggestions();
    loadGroups();
}

const confirmSuggestions = (suggestionIds: number[]) => runSequentially(suggestionIds, 'confirm');
const dismissSuggestions = (suggestionIds: number[]) => runSequentially(suggestionIds, 'dismiss');
const reopenSuggestions = (suggestionIds: number[]) => runSequentially(suggestionIds, 'reopen');

const groupUrl = (group: Group) => `/federations/${props.federationId}/record-links/groups/${group.id}`;

function reloadAfterGroupChange() {
    loadGroups();
    loadSuggestions();
}

function runGroupAction(request: Promise<unknown>, successMessage: string) {
    pendingAction.value = true;
    request
        .then(() => {
            notify.success(successMessage);
            reloadAfterGroupChange();
        })
        .catch((error) => notify.error(errorMessage(error)))
        .finally(() => {
            pendingAction.value = false;
        });
}

function setMain(group: Group, record: GroupRecord) {
    runGroupAction(
        axios.put(`${groupUrl(group)}/main`, { type: record.type, recordId: record.recordId }),
        i18n.t('Der Hauptdatensatz wurde festgelegt.'),
    );
}

function clearMain(group: Group) {
    runGroupAction(
        axios.put(`${groupUrl(group)}/main`, { type: 'member', recordId: null }),
        i18n.t('Die Festlegung des Hauptdatensatzes wurde aufgehoben.'),
    );
}

function askRemove(group: Group, record: GroupRecord) {
    removeCandidate.value = { group, record };
    removeDialogOpen.value = true;
}

function confirmRemove(event: { done: (_success: boolean) => void }) {
    const candidate = removeCandidate.value;
    if (candidate === null) {
        event.done(false);
        return;
    }

    axios.delete(`${groupUrl(candidate.group)}/records/${candidate.record.type}/${candidate.record.recordId}`)
        .then(() => {
            notify.success(i18n.t('Der Datensatz wurde aus der Verknüpfung entfernt.'));
            event.done(true);
            reloadAfterGroupChange();
        })
        .catch((error) => {
            notify.error(errorMessage(error));
            event.done(false);
        });
}

function askDissolve(group: Group) {
    dissolveTarget.value = group;
    dissolveDialogOpen.value = true;
}

function confirmDissolve(event: { done: (_success: boolean) => void }) {
    const group = dissolveTarget.value;
    if (group === null) {
        event.done(false);
        return;
    }

    axios.delete(groupUrl(group))
        .then(() => {
            notify.success(i18n.t('Die Verknüpfung wurde aufgelöst.'));
            event.done(true);
            reloadAfterGroupChange();
        })
        .catch((error) => {
            notify.error(errorMessage(error));
            event.done(false);
        });
}

function askAdd(group: Group) {
    addTargetGroup.value = group;
    addDialogOpen.value = true;
}

function addMembers(payload: MemberSearchSubmit) {
    const group = addTargetGroup.value;
    if (group === null) {
        payload.done(false);
        return;
    }

    const addSequentially = async () => {
        for (const memberId of payload.memberIds) {
            await axios.post(`${groupUrl(group)}/records`, { type: 'member', recordId: memberId });
        }
    };

    addSequentially()
        .then(() => {
            notify.success(i18n.t('Die Datensätze wurden zur Verknüpfung hinzugefügt.'));
            payload.done(true);
        })
        .catch((error) => {
            notify.error(errorMessage(error));
            payload.done(false);
        })
        .finally(reloadAfterGroupChange);
}

function createGroup(payload: MemberSearchSubmit) {
    axios.post(`/federations/${props.federationId}/record-links/groups`, {
        type: 'member',
        recordIds: payload.memberIds,
        note: payload.note === '' ? null : payload.note,
    })
        .then(() => {
            notify.success(i18n.t('Die Datensätze wurden verknüpft.'));
            payload.done(true);
            reloadAfterGroupChange();
        })
        .catch((error) => {
            notify.error(errorMessage(error));
            payload.done(false);
        });
}

// a subset of a cluster: the pairs among the chosen records close, the others stay open
const linkRecords = (payload: LinkRecordsSubmit) => createGroup({ memberIds: payload.memberIds, note: '', done: payload.done });
</script>

<template>
    <div>
        <div>
            <div class="kgr-row kgr-postfix kgr-postfix--between items-center">
                <SphTabs
                    v-model="activeTab"
                    :tabs="tabs"
                />
                <div
                    v-if="canManage"
                    class="flex gap-2"
                >
                    <SphButton
                        :label="$t('Scan starten')"
                        :loading="pendingAction"
                        :disabled="jobRunning"
                        @click="startScan"
                    />
                    <SphButton
                        :label="$t('Datensätze manuell verknüpfen')"
                        variant="primary"
                        @click="createDialogOpen = true"
                    />
                </div>
            </div>

            <div
                v-if="scanStatus !== null"
                class="kgr-alert kgr-alert--info kgr-row flex items-center gap-2"
            >
                <SphIcon
                    :icon="Loader"
                    class="shrink-0 animate-[spin_1.25s_linear_infinite]"
                />
                <span>
                    {{ scanStatus === 'running' ? $t('Der Scan läuft.') : $t('Der Scan ist eingeplant und startet in Kürze.') }}
                    {{ $t('Die Vorschläge werden nach Abschluss automatisch aktualisiert.') }}
                </span>
            </div>

            <div
                v-if="autoLinkStatus !== null"
                class="kgr-alert kgr-alert--info kgr-row flex items-center gap-2"
            >
                <SphIcon
                    :icon="Loader"
                    class="shrink-0 animate-[spin_1.25s_linear_infinite]"
                />
                <span>
                    {{ autoLinkStatus === 'running' ? $t('Die automatische Verknüpfung läuft.') : $t('Die automatische Verknüpfung ist eingeplant und startet in Kürze.') }}
                    {{ $t('Die Vorschläge werden nach Abschluss automatisch aktualisiert.') }}
                </span>
            </div>

            <div v-if="activeTab !== 'groups'">
                <div class="kgr-row suggestion-toolbar">
                    <SphButton
                        v-if="activeTab === 'suggestions' && canManage"
                        :label="`${$t('Alle exakten Treffer verknüpfen')} (${exactMatchCount})`"
                        variant="primary"
                        size="sm"
                        :disabled="pendingAction || jobRunning || exactMatchCount === 0"
                        @click="autoLinkDialogOpen = true"
                    />
                    <SphButton
                        v-if="hasDifferences"
                        :label="expandAll ? $t('Alle Abweichungen zuklappen') : $t('Alle Abweichungen aufklappen')"
                        size="sm"
                        @click="expandAll = !expandAll"
                    />
                    <span
                        v-if="!loadingSuggestions"
                        class="kgr-text kgr-text--light suggestion-toolbar__info"
                    >{{ scanInfo }}</span>
                </div>
                <div
                    v-if="activeTab === 'same-club'"
                    class="kgr-alert kgr-alert--info kgr-row"
                >
                    {{ $t('Doppelte Datensätze innerhalb eines Vereins können nicht verknüpft werden. Sie sollten im Verein bereinigt werden.') }}
                </div>
                <div
                    v-if="activeTab === 'dismissed'"
                    class="kgr-alert kgr-alert--info kgr-row"
                >
                    {{ $t('Verworfene Vorschläge werden vom Scan nicht erneut vorgeschlagen und nie automatisch verknüpft. Beim Aufheben einer Verknüpfung werden die betroffenen Paare automatisch verworfen.') }}
                </div>
                <SphSpinner
                    v-if="loadingSuggestions"
                    variant="centered"
                    class="record-links-loading"
                />
                <div
                    v-else-if="clusters.length === 0"
                    class="kgr-alert kgr-alert--info"
                >
                    <template v-if="activeTab === 'same-club'">
                        {{ $t('Keine Funde innerhalb einzelner Vereine.') }}
                    </template>
                    <template v-else-if="activeTab === 'dismissed'">
                        {{ $t('Keine verworfenen Vorschläge.') }}
                    </template>
                    <template v-else>
                        {{ $t('Keine offenen Vorschläge.') }}
                        <template v-if="canManage">
                            {{ $t('Starten Sie einen Scan, um mögliche Verknüpfungen zu finden.') }}
                        </template>
                    </template>
                </div>
                <template v-else>
                    <SuggestionsList
                        :clusters="clusters"
                        :can-manage="canManage"
                        :pending="pendingAction"
                        :mode="activeTab"
                        :expand-all="expandAll"
                        @confirm="confirmSuggestions"
                        @dismiss="dismissSuggestions"
                        @reopen="reopenSuggestions"
                        @link-records="linkRecords"
                    />
                    <SphPagination
                        :key="activeTab"
                        :page="suggestionsPage"
                        :items-per-page="PAGE_SIZE"
                        :total="suggestionsTotal"
                        class="mt-4"
                        @update:page="onSuggestionsPage"
                    />
                </template>
            </div>

            <div v-else-if="activeTab === 'groups'">
                <div class="kgr-row kgr-postfix kgr-postfix--between items-end">
                    <SphField :label="$t('Status')">
                        <SphSelect
                            v-model="filterState"
                            :options="stateOptions"
                            :placeholder="$t('Alle')"
                            clearable
                        />
                    </SphField>
                    <SphInput
                        v-model="filterSearch"
                        :placeholder="$t('Suche nach Name oder Verein ...')"
                    />
                </div>

                <div class="kgr-row suggestion-toolbar">
                    <SphButton
                        v-if="hasDifferences"
                        :label="expandAll ? $t('Alle Abweichungen zuklappen') : $t('Alle Abweichungen aufklappen')"
                        size="sm"
                        @click="expandAll = !expandAll"
                    />
                    <span
                        v-if="lastPipelineRunAt"
                        class="kgr-text kgr-text--light suggestion-toolbar__info"
                    >{{ $t('Zuletzt geprüft') }}: {{ lastPipelineRunAt }}</span>
                </div>

                <SphSpinner
                    v-if="loading"
                    variant="centered"
                    class="record-links-loading"
                />

                <div
                    v-else-if="groups.length === 0"
                    class="kgr-alert kgr-alert--info"
                >
                    {{ $t('Keine verknüpften Datensätze vorhanden.') }}
                </div>

                <template v-else>
                    <div class="group-cards kgr-row">
                        <GroupCard
                            v-for="group in groups"
                            :key="group.id"
                            :group="group"
                            :can-manage="canManage"
                            :pending="pendingAction"
                            :expand-all="expandAll"
                            @set-main="(record) => setMain(group, record)"
                            @clear-main="clearMain(group)"
                            @remove="(record) => askRemove(group, record)"
                            @add="askAdd(group)"
                            @dissolve="askDissolve(group)"
                        />
                    </div>
                    <SphPagination
                        :page="groupsPage"
                        :items-per-page="PAGE_SIZE"
                        :total="groupsTotal"
                        class="mt-4"
                        @update:page="onGroupsPage"
                    />
                </template>
            </div>
        </div>

        <SphDialog
            v-model:state="autoLinkDialogOpen"
            variant="form"
            :title="$t('Alle exakten Treffer verknüpfen')"
            :submit-button-label="$t('Alle exakten Treffer verknüpfen')"
            @submit="autoLink"
        >
            <p>{{ $t('Sollen alle exakten Treffer zwischen verschiedenen Vereinen ohne weitere Prüfung verknüpft werden? Betroffen sind {count} Personen.', { count: exactMatchCount }) }}</p>
        </SphDialog>

        <MemberSearchDialog
            v-model:state="createDialogOpen"
            :federation-id="federationId"
            mode="create"
            @submit="createGroup"
        />

        <MemberSearchDialog
            v-model:state="addDialogOpen"
            :federation-id="federationId"
            mode="add"
            @submit="addMembers"
        />

        <SphDialog
            v-model:state="removeDialogOpen"
            variant="form"
            :title="$t('Datensatz entfernen')"
            :submit-button-label="$t('Entfernen')"
            @submit="confirmRemove"
        >
            <p>{{ $t('Soll der Datensatz aus der Verknüpfung entfernt werden?') }}</p>
            <p v-if="(removeCandidate?.group.records.length ?? 0) <= 2">
                {{ $t('Die Verknüpfung wird dabei vollständig aufgelöst, da weniger als zwei Datensätze übrig bleiben.') }}
            </p>
        </SphDialog>

        <SphDialog
            v-model:state="dissolveDialogOpen"
            variant="form"
            :title="$t('Verknüpfung auflösen')"
            :submit-button-label="$t('Auflösen')"
            @submit="confirmDissolve"
        >
            <p>{{ $t('Soll die Verknüpfung vollständig aufgelöst werden? Die Mitgliedsdatensätze selbst bleiben erhalten.') }}</p>
        </SphDialog>
    </div>
</template>

<style scoped lang="scss">
.suggestion-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    background-color: var(--kgr-color-neutral-lighter);
}

.suggestion-toolbar__info {
    margin-left: auto;
}

.record-links-loading {
    min-height: 10rem;
}

.group-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(36rem, 100%), 1fr));
    align-items: start;
    gap: 0.875rem;
}
</style>
